import { useEffect, useState, useRef } from "react";
import { fetchGlossary, adminCreateGlossary, adminUpdateGlossary, adminDeleteGlossary, adminTranslateGlossary, adminGlossaryTranslateStatus } from "@/lib/api";
import { Plus, Pencil, Trash2, X, Languages, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { devanagari: "", transliteration: "", meaning: "", context: "", related: [] };
const inputCls = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";
const LANG_LABEL = { hi: "हि", ta: "த", te: "తె" };

export default function AdminGlossary() {
  const [terms, setTerms] = useState([]);
  const [editing, setEditing] = useState(null); // id or 'new'
  const [form, setForm] = useState(EMPTY);
  const [trJob, setTrJob] = useState(null); // {status, translated, total}
  const pollRef = useRef(null);

  const load = () => fetchGlossary().then(setTerms);
  useEffect(() => { load(); }, []);

  // Poll translation status while a job is running.
  const poll = () => adminGlossaryTranslateStatus().then((s) => {
    setTrJob(s);
    if (s.status === "running") {
      pollRef.current = setTimeout(poll, 2500);
    } else {
      if (s.status === "done") { toast("Translation complete", { description: `${s.translated || 0} entries translated (Hi · Ta · Te)` }); load(); }
      else if (s.status === "error") toast.error("Translation failed", { description: s.error });
    }
  }).catch(() => {});
  useEffect(() => { adminGlossaryTranslateStatus().then((s) => { setTrJob(s); if (s.status === "running") poll(); }).catch(() => {}); return () => clearTimeout(pollRef.current); }, []); // eslint-disable-line

  const runTranslate = async () => {
    try {
      const r = await adminTranslateGlossary();
      if (r.started === false && r.status === "running") toast("Translation already running");
      else toast("Rescan started", { description: "Translating new/changed terms into Hindi, Tamil & Telugu…" });
      setTrJob({ status: "running", translated: 0, total: 0 });
      clearTimeout(pollRef.current);
      pollRef.current = setTimeout(poll, 2000);
    } catch { toast.error("Could not start translation"); }
  };

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (term) => { setForm({ ...EMPTY, ...term }); setEditing(term.id); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    const payload = { ...form };
    if (editing === "new") await adminCreateGlossary(payload);
    else await adminUpdateGlossary(editing, payload);
    toast("Glossary saved", { description: form.devanagari });
    close();
    load();
  };

  const remove = async (id, dev) => {
    if (!window.confirm(`Delete "${dev}"?`)) return;
    await adminDeleteGlossary(id);
    toast("Term deleted");
    load();
  };

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="font-serif-latin text-3xl text-ink">Glossary</h1>
        <div className="flex items-center gap-2">
          <button onClick={runTranslate} disabled={trJob?.status === "running"} data-testid="glossary-translate"
            className="inline-flex items-center gap-2 border border-saffron/50 px-4 py-2.5 text-sm font-medium text-saffron transition-colors hover:bg-saffron/10 disabled:opacity-60">
            {trJob?.status === "running"
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Translating {trJob.total ? `${trJob.translated}/${trJob.total}` : ""}</>
              : <><Languages className="h-4 w-4" /> Rescan &amp; Translate</>}
          </button>
          <button onClick={openNew} data-testid="glossary-new" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
            <Plus className="h-4 w-4" /> New Term
          </button>
        </div>
      </div>

      {editing && (
        <div className="mb-8 border border-line bg-surface p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-serif-latin text-xl text-ink">{editing === "new" ? "New Term" : "Edit Term"}</h3>
            <button onClick={close} className="text-ink-3 hover:text-ink"><X className="h-4 w-4" /></button>
          </div>
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <input value={form.devanagari} onChange={(e) => setForm({ ...form, devanagari: e.target.value })} placeholder="देवनागरी" className={`${inputCls} font-deva-body`} data-testid="glossary-devanagari" />
              <input value={form.transliteration} onChange={(e) => setForm({ ...form, transliteration: e.target.value })} placeholder="Transliteration" className={inputCls} />
            </div>
            <textarea value={form.meaning} onChange={(e) => setForm({ ...form, meaning: e.target.value })} placeholder="Meaning" rows={2} className={inputCls} />
            <textarea value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} placeholder="Context" rows={2} className={inputCls} />
            <input value={form.related.join(", ")} onChange={(e) => setForm({ ...form, related: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Related slugs (comma separated)" className={inputCls} />
            <div className="flex justify-end gap-2">
              <button onClick={close} className="px-4 py-2 text-sm text-ink-2">Cancel</button>
              <button onClick={save} data-testid="glossary-save" className="bg-[var(--saffron)] px-5 py-2 text-sm font-medium text-white hover:bg-[var(--saffron-dark)]">Save</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {terms.map((term) => (
          <div key={term.id} className="flex items-start justify-between gap-3 border border-line bg-surface p-4">
            <div>
              <p className="font-deva text-xl text-ink">{term.devanagari} <span className="text-sm text-gold">— {term.transliteration}</span></p>
              <p className="mt-1 text-sm text-ink-2 line-clamp-2">{term.meaning}</p>
              {term.meaning && (
                <div className="mt-2 flex gap-1.5" data-testid={`glossary-langs-${term.id}`}>
                  {["hi", "ta", "te"].map((lg) => {
                    const has = (term.meaning_i18n || {})[lg];
                    return <span key={lg} title={has ? `${lg} translated` : `${lg} missing`} className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded px-1 text-[11px] font-medium ${has ? "bg-emerald-500/15 text-emerald-700" : "bg-ink/5 text-ink-3"}`}>{LANG_LABEL[lg]}</span>;
                  })}
                </div>
              )}
            </div>
            <div className="flex shrink-0 gap-1">
              <button onClick={() => openEdit(term)} className="p-1.5 text-ink-3 hover:text-saffron"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(term.id, term.devanagari)} className="p-1.5 text-ink-3 hover:text-[var(--saffron-dark)]"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
